import React from "react";
import { isMobile, isMobileOnly, isTablet } from "react-device-detect";
import styled, { css } from "styled-components";
import { FocusModeSidebarContentType } from "@lifeat/shared/system/AppModeTypes";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import GA4Event, { GA4EventProperties } from "@analytics/GoogleAnalytics/GA4Event";
import * as GA4Service from "@analytics/GoogleAnalytics/GA4Service";
import { useAppearanceContext } from "@contexts/AppearanceContext";
import { CallToActionContextType, useCallToActionContext } from "@contexts/CallToActionContext";
import { UserContextType, useUserContext } from "@contexts/UserContext";
import Styles from "@themes/Styles";
import { SignUpCTASource } from "@user/AmplitudeUserEvents";
import { ToggleWidgetEvent } from "../../AmplitudeWidgetToolbarEvents";
import { WidgetDataType, WidgetEntryType, WidgetName } from "../../WidgetDataManager";
import { useBreathingWidgetContext } from "../Widget/BreathingWidget/BreathingWidgetContext";
import ToolbarSettings from "./ToolbarSettings";
import WidgetToolbarElement from "./WidgetToolbarElement";
import WidgetToolbarIcon from "./WidgetToolbarIcon";

const LeftNavContainer = styled.div<{ isHidden: boolean }>`
    position: fixed;
    z-index: ${Styles.zIndexWidgetContents};
    gap: 8px;
    width: 70px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin: 8px;
    top: ${Styles.topBarAndPaddingHeight};
    left: ${(props) => (props.isHidden ? `calc(-70px - 8px)` : 0)};
    transition: left 0.3s ease-out;
`;

const LeftNavContentContainer = styled.div`
    width: 70px;
    background-color: ${(props) => props.theme.backgroundPrimary};
    display: flex;
    flex-direction: column;
    align-items: center;
    border-radius: 7px;
`;

const WidgetRowContainer = styled.div<{ hideScrollbar: boolean; isTouchDevice: boolean }>`
    padding: 8px 0;
    display: flex;
    flex-direction: column;
    width: 60px;

    align-items: center;
    gap: 10px;

    max-height: 300px;
    overflow-y: scroll;
    overflow-x: hidden;

    ${({ hideScrollbar, isTouchDevice }) =>
        !hideScrollbar &&
        css`
            ::-webkit-scrollbar-thumb {
                display: ${isTouchDevice ? `block` : `none`};
                border-radius: 8px;
                background-color: ${(props) => props.theme.controlPrimary};
                border: 2px ${(props) => props.theme.backgroundPrimary} solid;
            }

            ::-webkit-scrollbar {
                display: block;
                width: 10px;
            }

            &:hover {
                ::-webkit-scrollbar-thumb,
                ::-webkit-scrollbar {
                    display: block;
                }
            }

            margin-left: 10px; //this is to account for the scrollbar on the right, so that the content is still centered
        `}
`;

interface WidgetToolbarProps {
    widgetData: WidgetDataType;
    openWidgetKeys: Set<string>;
    toggleWidget: (id: string) => void;
    sidebarContent: FocusModeSidebarContentType;
    sidebarOpen: boolean;
    toggleSpacesSidebar: () => void;
    toggleCalendarSidebar: () => void;
    toggleSoundboardSidebar: () => void;
    toggleAppsSidebar: () => void;
    closeAndResetAllWidgets: () => void;
}

export default function WidgetToolbar(props: WidgetToolbarProps) {
    const amplitudeService = AmplitudeService.getInstance();
    const { user, userData }: UserContextType = useUserContext();
    const { setSignInView }: CallToActionContextType = useCallToActionContext();
    const { isFocusModeActive } = useAppearanceContext();
    const breathingWidget = useBreathingWidgetContext();

    const { SpacesWidget, CalendarWidget, SoundboardWidget, ...remainingWidgets } = props.widgetData;

    const logToggleWidgetEvent = (widgetName: WidgetName) => {
        amplitudeService.logEvent(new ToggleWidgetEvent(ToggleWidgetEvent.SOURCES.TOOLBAR, true, widgetName));

        GA4Service.logEvent(GA4Event.TOGGLE_WIDGET, {
            [GA4EventProperties.widget_name]: widgetName,
            [GA4EventProperties.toggle_widget.key]: GA4EventProperties.toggle_widget.open,
        });
    };

    const getWidgetToolbarElement = (id: string, widgetEntry: WidgetEntryType | undefined) => {
        if (widgetEntry) {
            let handleClick = () => props.toggleWidget(id);
            let isOpen = false;
            if (widgetEntry.requireUserAccount && (!user?.emailVerified || !userData)) {
                // If the widget requires a user account, but there isn't one
                // created, do not allow the user to toggle the widget.
                handleClick = () =>
                    id === "CalendarWidget"
                        ? setSignInView(SignUpCTASource.CALENDAR_SIDEBAR)
                        : setSignInView(SignUpCTASource.TOOLBAR);
            } else {
                let openWidget: () => void;
                switch (id) {
                    case "SpacesWidget":
                        openWidget = () => props.toggleSpacesSidebar();
                        isOpen = props.sidebarContent === "spaces" ? props.sidebarOpen : false;
                        break;
                    case "CalendarWidget":
                        openWidget = () => props.toggleCalendarSidebar();
                        isOpen = props.sidebarContent === "calendar" ? props.sidebarOpen : false;
                        break;
                    case "SoundboardWidget":
                        openWidget = () => props.toggleSoundboardSidebar();
                        isOpen = props.sidebarContent === "soundboard" ? props.sidebarOpen : false;
                        break;
                    case "AppsWidget":
                        openWidget = () => props.toggleAppsSidebar();
                        isOpen = props.sidebarContent === "apps" ? props.sidebarOpen : false;
                        break;
                    case "BreathingWidget":
                        openWidget = () => breathingWidget.open();
                        break;
                    default:
                        openWidget = () => props.toggleWidget(id);
                        isOpen = props.openWidgetKeys.has(id);
                }
                handleClick = () => {
                    if (!isOpen) {
                        logToggleWidgetEvent(widgetEntry.name);
                    }

                    openWidget();
                };
            }

            const icon = <WidgetToolbarIcon name={widgetEntry.name} icon={widgetEntry.icon} isOpen={isOpen} />;

            return (
                <WidgetToolbarElement
                    key={id}
                    id={id}
                    name={widgetEntry.name}
                    icon={icon}
                    isOpen={isOpen}
                    handleClick={handleClick}
                    requiresUserAccount={widgetEntry.requireUserAccount}
                    category={widgetEntry.category}
                />
            );
        } else {
            return null;
        }
    };

    const toolbarWidgets = Object.entries(remainingWidgets).map(([id, widgetEntry]: [string, WidgetEntryType]) => {
        if (widgetEntry.isAccessibleOnMobile || !isMobileOnly) {
            return getWidgetToolbarElement(id, widgetEntry);
        } else {
            return null;
        }
    });

    const spacesButton = getWidgetToolbarElement("SpacesWidget", SpacesWidget);
    const calendarButton = getWidgetToolbarElement("CalendarWidget", CalendarWidget);
    const soundboardButton = getWidgetToolbarElement("SoundboardWidget", SoundboardWidget);

    return (
        <LeftNavContainer isHidden={isFocusModeActive ?? false}>
            <LeftNavContentContainer>
                <WidgetRowContainer hideScrollbar={isMobile} isTouchDevice={isMobile || isTablet}>
                    {spacesButton}
                    {soundboardButton}
                    {calendarButton}
                </WidgetRowContainer>
            </LeftNavContentContainer>
            <LeftNavContentContainer>
                <WidgetRowContainer hideScrollbar={isMobile} isTouchDevice={isMobile || isTablet}>
                    {toolbarWidgets}
                </WidgetRowContainer>
                <ToolbarSettings closeAndResetAllWidgets={props.closeAndResetAllWidgets} horizontalButton={true} />
            </LeftNavContentContainer>
        </LeftNavContainer>
    );
}
